import { decorate, action, computed, observable } from 'mobx'
import autoBind from "auto-bind"

import ApiClient from "utils/ApiClient";

import { makeKey } from '../lib/utils';

class AssistantStore {
  docked = false;
  opened = false;

  isAssistantButtonVisible = false;
  currentView = 'CHAT_HOME';

  accessToken = {
    token: null,
    expires: null
  }

  packs = [];
  packIdsByName = {};
  packsById = {};

  sheets = [];
  sheetIdsByName = {};
  sheetsById = {}

  #listConfigs = {};
  #columnsByName = {};

  #sheetRoles = {};

  currentSheetId = null;
  currentPackId = null;

  constructor(appStore) {
    this.appStore = appStore;
    autoBind(this)
  }

  get listConfigs() {
    return this.#listConfigs
  }

  get columnsByName() {
    return this.#columnsByName;
  }

  get sheetRoles() {
    return this.#sheetRoles
  }

  getPackByName(packName) {
    const packId = this.packIdsByName[makeKey(packName)];
    if (!packId) return;
    return this.packsById[packId];
  }

  getSheetByName(sheetName) {
    const sheetId = this.sheetIdsByName[makeKey(sheetName)];
    if (!sheetId) return;
    return this.sheetsById[sheetId];
  }

  getColumnByName(columnName, sheetId) {
    const sheetColumnsObj = this.columnsByName[sheetId]
    if (!sheetColumnsObj) return;
    return sheetColumnsObj[makeKey(columnName)]
  }

  async getSheetRoles(sheetId) {
    const roles = this.#sheetRoles[sheetId];
    if (roles) return roles;

    const res = await this.fetchSheetRoles({ list_id: sheetId });
    if (!res.data) return [];

    this.setSheetRoles(sheetId, res.data)
    return res.data
  }

  getListConfig(sheetId) {
    return this.#listConfigs[sheetId]
  }

  setSheets(sheets) {
    this.sheets = sheets
    const sheetsById = {}
    const sheetIdsByName = {};
    for (const sheet of sheets) {
      sheetsById[sheet.id] = sheet;
      sheetIdsByName[makeKey(sheet.name)] = sheet.id;
    }

    this.sheetIdsByName = { ...this.sheetIdsByName, ...sheetIdsByName }
    this.sheetsById = { ...this.sheetsById, ...sheetsById }
  }

  setCurrentPackId(packId) {
    if (packId === this.currentPackId) return;
    this.currentPackId = packId
  }

  setPacks(packs) {
    this.packs = packs
    const packsById = {};
    const packIdsByName = {};
    for (const pack of packs) {
      packsById[pack.id] = pack;
      packIdsByName[makeKey(pack.name)] = pack.id;
    }

    this.packIdsByName = { ...this.packIdsByName, ...packIdsByName }
    this.packsById = { ...this.packsById, ...packsById }
  }

  setListConfigs(sheetId, listConfigs) {
    this.#listConfigs[sheetId] = listConfigs

    const { listData = [] } = listConfigs;

    const columnsByName = {};
    for (const field of listData) {
      columnsByName[makeKey(field.name)] = field;
    }

    this.#columnsByName[sheetId] = columnsByName
  }

  setSheetRoles(sheetId, sheetRoles) {
    this.#sheetRoles[sheetId] = sheetRoles
  }

  setCurrentSheetId(sheetId) {
    this.currentSheetId = sheetId
  }

  /**
   * 
   * @param {{name:string; id?:number; icon?:string; color?:string;}} params 
   * @returns 
   */
  savePack(params) {
    return ApiClient.Packs.savePack(params);
  }

  fetchPacks() {
    return ApiClient.Packs.getPacks()
  }

  fetchSheets() {
    return ApiClient.Items.getAllItems();
  }

  fetchListConfig = (body) => {
    return ApiClient.List.getListConfig(body)
  }

  async fetchSheetRoles({ list_id }) {
    const res = await window.TL.ApiClient.ListRoles.getRoles({
      list_id,
    });
    return res;
  }

  addEventHandler(params) {
    return window.TL.ApiClient.EventHandlers.addHandler(params);
  };

  addColumnsToSheet({
    add_button_text,
    columns,
    list_id,
    list_name,
    parent_id,
  }) {
    return ApiClient.List.addEditColumn({
      data: columns,
      list_id,
      list_name: list_name || "",
      add_button_text: add_button_text || "",
      parent_id: parent_id || "",
    });
  }

  createSheet(text) {
    return ApiClient.List.createSheet({
      text,
    });
  };

  createList({ parent_id, list_name, columns, add_button_text }) {
    return ApiClient.List.createList(
      parent_id,
      list_name || "",
      columns,
      add_button_text || "",
    );
  }

  async addSheetToPack({ list_id, pack_id }) {
    const response = await ApiClient.Packs.addSheet({
      list_id,
      pack_id,
    });
    const sheetData = response.data;
    if(response.success && sheetData.is_log_sheet == '0' && sheetData.parent_list_id == '0' && this.appStore && this.appStore.allUserLists && Array.isArray(this.appStore.allUserLists) && this.appStore.allUserLists.length > 0) {
      this.appStore.allUserLists = [...this.appStore.allUserLists, sheetData];
    }
    return response;
  };

  dockChat() {
    this.docked = true;
  }

  unDockChat() {
    this.docked = false;
  }

  hideAssistantButton() {
    this.unDockChat()
    this.closeChat()
    this.isAssistantButtonVisible = false
  }

  showAssistantButton() {
    this.isAssistantButtonVisible = true
  }

  openChat() {
    this.opened = true;
    const beaconContainer = document.getElementById('beacon-container');
    if (beaconContainer) {
      beaconContainer.style.display = 'none';
    }
    const fcFrame = document.getElementById('fc_frame');
    if (fcFrame) {
      fcFrame.style.transition = 'margin 500ms';
      fcFrame.style.marginRight = '450px';
    }
  }

  closeChat() {
    this.opened = false;
    const beaconContainer = document.getElementById('beacon-container');
    if (beaconContainer) {
      beaconContainer.style.display = 'block';
    }
    const fcFrame = document.getElementById('fc_frame');
    if (fcFrame) {
      fcFrame.style.transition = 'margin 500ms';
      fcFrame.style.marginRight = '0';
    }
  }

  changeView(view) {
    this.currentView = view;
  }


  async fetchAndLoadPacks() {
    const res = await this.fetchPacks();
    if (res && res.data) {
      this.setPacks(res.data);
    }
    return res;
  };

  async fetchAndLoadSheets() {
    const res = await this.fetchSheets();
    if (res && res.data) {
      this.setSheets(res.data);
    }
    return res
  };

  /**
   * 
   * @param {{sheetId:}} body 
   * @returns 
   */
  async fetchAndLoadSheetConfig(sheetId) {
    const res = await this.fetchListConfig({ list_id: sheetId });
    if (res && res.data) {
      this.setListConfigs(sheetId, res.data);
    }
    return res
  };


  async getChatsAccessToken() {
    if (!this.accessToken.token) {
      const accessToken = await this.appStore.chatHelpersStore.getChatsAccessTokenHelper()
      this.accessToken = accessToken
      return accessToken;
    }

    const twoMinutesFromNow = Date.now() + 1000 * 60 * 2;
    if (twoMinutesFromNow >= this.accessToken.expires)
      return this.appStore.chatHelpersStore.getChatsAccessTokenHelper();

    return this.accessToken;
  };

}

decorate(AssistantStore, {
  docked: observable,
  opened: observable,
  dialogOpened: observable,
  currentView: observable,
  isAssistantButtonVisible: observable,
  //
  dockChat: action,
  unDockChat: action,
  openChat: action,
  closeChat: action,
  changeView: action,
  hideAssistantButton: action,
  showAssistantButton: action,
  //
  listConfigs: computed,
  columnsByName: computed,
  sheetRoles: computed,
  currentPackId: observable,
  currentSheetId: observable,

  packs: observable,
  sheetsIdByName: observable,
  sheetsById: observable,
  sheets: observable,
  packIdsByName: observable,
  packsById: observable,

  getSheetRoles: action,

  setSheets: action,
  setPacks: action,
  setListConfigs: action,
  getListConfig: action,
  setSheetRoles: action,
  setCurrentSheet: action,
  setCurrentPackId: action,

  fetchAndLoadPacks: action,
  fetchAndLoadSheets: action,
  fetchAndLoadSheetConfig: action,

  getPackByName: action,
  getSheetByName: action,
  getColumnByName: action
})

export default AssistantStore;